import React, { createContext, useContext, useState, useEffect } from 'react';
import { Download, Share, PlusSquare, CheckCircle, X, Smartphone, Monitor, RefreshCw, Sparkles } from 'lucide-react';

// Global reference to capture beforeinstallprompt early (before React mounts)
let globalDeferredPrompt = null;

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        globalDeferredPrompt = e;
        console.log('[PWA] Captured beforeinstallprompt event globally');
    });
}

const PWAContext = createContext({
    isInstallable: false,
    isInstalled: false,
    isIOS: false,
    portalType: 'supplier',
    promptInstall: () => {},
    checkForUpdates: () => {},
    forceUpdateApp: () => {},
    openInstallModal: () => {},
    closeInstallModal: () => {},
    launchApp: () => {}
});

export const PWAProvider = ({ children }) => {
    const [deferredPrompt, setDeferredPrompt] = useState(() => globalDeferredPrompt);
    const [isInstallable, setIsInstallable] = useState(() => !!globalDeferredPrompt);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showBanner, setShowBanner] = useState(false);
    const [portalType, setPortalType] = useState('supplier');
    const [isUpdating, setIsUpdating] = useState(false);

    // 1. Dynamic Manifest & Meta tag updates based on current route
    useEffect(() => {
        const updateManifestAndMeta = () => {
            const path = window.location.pathname;
            const isCustomerRoute = path.startsWith('/shop') || path.startsWith('/order') || path.startsWith('/status');
            const type = isCustomerRoute ? 'customer' : 'supplier';
            setPortalType(type);

            // Update Manifest Link dynamically so standalone app launch matches current portal context
            let manifestLink = document.querySelector('link[rel="manifest"]');
            if (manifestLink) {
                manifestLink.setAttribute('href', isCustomerRoute ? '/manifest.json' : '/supplier-manifest.json');
            }

            // Update Theme Color
            let themeMeta = document.querySelector('meta[name="theme-color"]');
            if (themeMeta) {
                themeMeta.setAttribute('content', isCustomerRoute ? '#06b6d4' : '#0f172a');
            }

            // Update Apple Title
            let appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
            if (appleTitleMeta) {
                appleTitleMeta.setAttribute('content', isCustomerRoute ? 'Yaseero Shop' : 'Yaseero Supplier');
            }
        };

        updateManifestAndMeta();
        window.addEventListener('popstate', updateManifestAndMeta);
        return () => window.removeEventListener('popstate', updateManifestAndMeta);
    }, []);

    // 2. Detect installation status & BeforeInstallPrompt event
    useEffect(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
        if (isStandalone) {
            setIsInstalled(true);
            setIsInstallable(false);
            setShowBanner(false);
        } else {
            setIsInstalled(false);
            const isDismissed = localStorage.getItem('pwa_banner_dismissed');
            if (!isDismissed) {
                setShowBanner(true);
            }
        }

        // Detect iOS
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
        setIsIOS(isIosDevice);

        if (isIosDevice && !isStandalone) {
            setIsInstallable(true);
        }

        if (globalDeferredPrompt) {
            setDeferredPrompt(globalDeferredPrompt);
            setIsInstallable(true);
        }

        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            globalDeferredPrompt = e;
            setDeferredPrompt(e);
            setIsInstallable(true);
            const isDismissed = localStorage.getItem('pwa_banner_dismissed');
            if (!isStandalone && !isDismissed) {
                setShowBanner(true);
            }
        };

        const handleAppInstalled = () => {
            if (window.matchMedia('(display-mode: standalone)').matches) {
                setIsInstalled(true);
            }
            setIsInstallable(false);
            setDeferredPrompt(null);
            globalDeferredPrompt = null;
            setShowBanner(false);
            console.log('[PWA] App installed event received');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const checkForUpdates = async () => {
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.getRegistration();
                if (registration) {
                    console.log('[PWA] Checking for Service Worker updates...');
                    await registration.update();
                }
            } catch (err) {
                console.error('[PWA] Error checking for updates:', err);
            }
        }
    };

    // Force hard refresh & clear all PWA caches
    const forceUpdateApp = async () => {
        setIsUpdating(true);
        try {
            console.log('[PWA] Force clearing caches & service workers for hard update...');
            
            // 1. Unregister Service Workers
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (const registration of registrations) {
                    try {
                        if (registration.active) {
                            registration.active.postMessage({ type: 'CLEAR_ALL_CACHES' });
                        }
                        await registration.unregister();
                    } catch (e) {
                        console.warn('[PWA] Error unregistering service worker:', e);
                    }
                }
            }

            // 2. Delete all CacheStorage caches
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            // 3. Clear version reload locks
            localStorage.removeItem('yaseero_last_applied_build_hash');
            sessionStorage.removeItem('yaseero_last_auto_reload_ts');

            // 4. Force hard reload with timestamp cache buster
            setTimeout(() => {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set('pwa_update', Date.now().toString());
                window.location.href = currentUrl.toString();
            }, 400);
        } catch (err) {
            console.error('[PWA] Error during force update:', err);
            window.location.reload(true);
        }
    };

    const launchApp = () => {
        const targetUrl = window.location.origin + (portalType === 'supplier' ? '/admin' : '/shop');
        window.open(targetUrl, '_blank');
        setIsModalOpen(false);
    };

    const promptInstall = () => {
        const activePrompt = deferredPrompt || globalDeferredPrompt;
        if (activePrompt) {
            try {
                activePrompt.prompt();
                activePrompt.userChoice.then((choiceResult) => {
                    if (choiceResult.outcome === 'accepted') {
                        console.log('[PWA] User accepted the install prompt');
                        setShowBanner(false);
                    } else {
                        console.log('[PWA] User dismissed the install prompt');
                    }
                    setDeferredPrompt(null);
                    globalDeferredPrompt = null;
                    setIsModalOpen(false);
                }).catch((err) => {
                    console.error('[PWA] Error handling userChoice:', err);
                });
            } catch (err) {
                console.warn('[PWA] Error calling prompt(), fallback to modal:', err);
                setIsModalOpen(true);
            }
        } else {
            setIsModalOpen(true);
        }
    };

    const dismissBanner = () => {
        setShowBanner(false);
        localStorage.setItem('pwa_banner_dismissed', 'true');
    };

    const openInstallModal = () => setIsModalOpen(true);
    const closeInstallModal = () => setIsModalOpen(false);

    const isSupplier = portalType === 'supplier';

    return (
        <PWAContext.Provider
            value={{
                isInstallable,
                isInstalled,
                isIOS,
                portalType,
                promptInstall,
                checkForUpdates,
                forceUpdateApp,
                openInstallModal,
                closeInstallModal,
                launchApp
            }}
        >
            {children}

            {/* Floating PWA Install Prompt Banner */}
            {showBanner && !isInstalled && (
                <div className="fixed bottom-20 lg:bottom-6 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 bg-slate-900 text-white p-3.5 rounded-2xl shadow-2xl border border-slate-700/60 flex items-center justify-between gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-white rounded-xl p-0.5 shrink-0 overflow-hidden shadow">
                            <img src="/logo-192.png" alt="App Logo" className="w-full h-full object-cover rounded-lg" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-black tracking-wide text-white truncate">
                                {isSupplier ? 'Install Supplier App' : 'Install Yaseero Shop'}
                            </p>
                            <p className="text-[10px] text-slate-300 truncate">Install app for fast 1-tap access</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <button
                            onClick={promptInstall}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-xs px-3.5 py-2 rounded-xl transition-all shadow-md active:scale-95 flex items-center gap-1.5 cursor-pointer"
                        >
                            <Download size={14} />
                            Install
                        </button>
                        <button
                            onClick={dismissBanner}
                            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                            title="Dismiss"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Global PWA Install & App Update Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100 dark:border-slate-700">
                        {/* Header */}
                        <div className={`p-6 text-white relative ${isSupplier ? 'bg-slate-900' : 'bg-cyan-600'}`}>
                            <button
                                onClick={closeInstallModal}
                                className="absolute top-4 right-4 p-2 text-white/70 hover:text-white rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 bg-white rounded-2xl p-1 shadow-md flex items-center justify-center">
                                    <img src="/logo-192.png" alt="Yaseero App Icon" className="w-full h-full object-cover rounded-xl" />
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-lg text-white">
                                        {isSupplier ? 'Yaseero Supplier App' : 'Yaseero Customer Ordering'}
                                    </h3>
                                    <p className="text-xs text-white/80 font-medium">Application details & update control</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            {isInstalled ? (
                                <div className="text-center py-2 space-y-4">
                                    <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-inner">
                                        <CheckCircle size={32} />
                                    </div>
                                    <div>
                                        <h4 className="font-extrabold text-slate-800 dark:text-slate-100 text-base">App Installed & Active</h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                                            You are currently running the PWA standalone app.
                                        </p>
                                    </div>

                                    {/* Prominent Force Update Action for Installed PWA */}
                                    <button
                                        onClick={forceUpdateApp}
                                        disabled={isUpdating}
                                        className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm rounded-2xl shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                    >
                                        <RefreshCw size={18} className={isUpdating ? 'animate-spin' : ''} />
                                        <span>{isUpdating ? 'Clearing Cache & Updating...' : 'Re-Sync & Force Update Latest Version'}</span>
                                    </button>

                                    <button
                                        onClick={closeInstallModal}
                                        className="w-full py-2.5 px-4 text-slate-600 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl cursor-pointer border border-slate-200 dark:border-slate-700"
                                    >
                                        Close Window
                                    </button>
                                </div>
                            ) : (deferredPrompt || globalDeferredPrompt) ? (
                                <div className="space-y-4">
                                    <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700 space-y-2">
                                        <div className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-200">
                                            <Monitor size={16} className="text-blue-500" />
                                            <span>One-Click Desktop & Mobile App</span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                            Enjoy full-screen access, fast loading, offline reliability, and instant launch from your taskbar or launcher.
                                        </p>
                                    </div>

                                    <button
                                        onClick={promptInstall}
                                        className={`w-full py-3.5 px-4 rounded-2xl text-white font-extrabold text-sm shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                            isSupplier 
                                                ? 'bg-slate-900 hover:bg-slate-800 shadow-slate-900/20' 
                                                : 'bg-cyan-500 hover:bg-cyan-600 shadow-cyan-500/20'
                                        }`}
                                    >
                                        <Download size={18} />
                                        <span>Install Application Now</span>
                                    </button>
                                </div>
                            ) : isIOS ? (
                                <div className="space-y-4">
                                    <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-2xl border border-amber-200/50 dark:border-amber-900/40 text-xs text-amber-900 dark:text-amber-200 space-y-3">
                                        <p className="font-extrabold text-sm flex items-center gap-2">
                                            <Smartphone size={18} className="text-amber-600" />
                                            Install on iOS (Safari)
                                        </p>
                                        <ol className="list-decimal pl-4 space-y-2 font-medium">
                                            <li className="flex items-center gap-1.5">
                                                <span>Tap the </span>
                                                <span className="font-bold flex items-center gap-1 bg-white dark:bg-slate-700 px-2 py-0.5 rounded border border-amber-200">
                                                    <Share size={14} className="text-blue-500" /> Share
                                                </span>
                                                <span> button in Safari</span>
                                            </li>
                                            <li className="flex items-center gap-1.5">
                                                <span>Scroll down and select </span>
                                                <span className="font-bold flex items-center gap-1 bg-white dark:bg-slate-700 px-2 py-0.5 rounded border border-amber-200">
                                                    <PlusSquare size={14} className="text-slate-700 dark:text-slate-200" /> Add to Home Screen
                                                </span>
                                            </li>
                                            <li>Confirm by clicking <strong>Add</strong> in top right.</li>
                                        </ol>
                                    </div>
                                    <button
                                        onClick={closeInstallModal}
                                        className="w-full py-3 text-slate-600 dark:text-slate-300 font-bold text-sm hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl cursor-pointer"
                                    >
                                        Got It
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4 text-center py-2">
                                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-bold">
                                        To install <strong>{isSupplier ? 'Yaseero Supplier Portal' : 'Yaseero Customer Portal'}</strong> on your mobile home screen:
                                    </p>
                                    <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-2xl text-xs text-slate-600 dark:text-slate-300 text-left space-y-2.5 border border-slate-100 dark:border-slate-700">
                                        <p className="font-extrabold text-slate-800 dark:text-slate-100">📱 Android (Chrome):</p>
                                        <ol className="list-decimal pl-4 space-y-1.5 text-slate-600 dark:text-slate-300 font-medium">
                                            <li>Tap the <strong>⋮ Menu</strong> (three dots) in top-right of Chrome.</li>
                                            <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
                                            <li>Tap <strong>Install</strong> to add the native app icon to your app drawer.</li>
                                        </ol>
                                        <hr className="border-slate-200 dark:border-slate-600 my-2" />
                                        <p className="font-extrabold text-slate-800 dark:text-slate-100">🌐 Desktop (Chrome / Edge):</p>
                                        <p className="pl-4 text-slate-600 dark:text-slate-300">Click the <strong>Install</strong> icon in the address bar (top-right).</p>
                                    </div>
                                    <button
                                        onClick={closeInstallModal}
                                        className="w-full py-3 text-slate-600 dark:text-slate-300 font-bold text-sm hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl cursor-pointer border border-slate-200 dark:border-slate-700"
                                    >
                                        Got It
                                    </button>
                                </div>
                            )}

                            {/* Global Bottom Force Refresh Option */}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-700 text-center">
                                <button
                                    onClick={forceUpdateApp}
                                    disabled={isUpdating}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-bold flex items-center justify-center gap-1.5 mx-auto cursor-pointer disabled:opacity-50"
                                >
                                    <RefreshCw size={13} className={isUpdating ? 'animate-spin' : ''} />
                                    <span>Not seeing latest changes? Tap to Force Update App</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PWAContext.Provider>
    );
};

export const usePWA = () => useContext(PWAContext);
